import {
  getInteriorCodeFromFactory,
  getInteriorCodeFromOptions,
  getVarnishCodeFromFactory,
  getVarnishCodeFromOptions,
  INextArticle,
  IOrder,
  mapVatPricesForNext,
} from 'next-common';
import { VehicleConfig } from './vehicleOrder';
import { remove, cloneDeep, isEmpty } from 'lodash';
import {
  calculatePricesBasedOnArticleList,
  ICalculatePricesBasedOnArticleListResult,
  mapVehicleApplicationCodeStringToEnum,
} from 'sales-common-calculations';
import { IVehicleModel } from '../../components/sales/sales-vehicle-picker-page';
import { ArticleType, VatCode } from 'sales-types-common';
import { VehicleCondition } from 'sales-types-common';
import { getErrorMessageForError, ValidationErrorCode } from '@components/notifications';
import { ICartPricesCustom, IVatCalculationProps } from '@common/hooks/useVatCalculation';
import { CartPrices } from 'types';

export interface ICartPricesExtended extends CartPrices {
  scrapDeposit?: number;
  oneOffTax?: number;
  calculatedBasePriceExclVatAndTax?: number;
  calculatedSumOfDiscountsExclVat?: number;
}

export const prepareArticles = (
  key = '',
  price = 0,
  vatCode = VatCode.NOT_APPLICABLE.toString(),
  articleType = null,
  dealerPriceExclVat = 0,
  isRecommended = false,
  articles: INextArticle[] = [],
  isRemove = false,
  id: string = '',
  discountPercentage = 0,
  discountAmountExclVat = 0
) => {
  const existingArticles = cloneDeep(articles);
  const keyIndex = existingArticles?.findIndex((article) => article?.key === key);
  const priceExclVat = isRecommended ? 0 : price;
  if (keyIndex >= 0) {
    if (isRemove) {
      existingArticles?.splice(keyIndex, 1);
    } else if (key === VehicleConfig.VARIANT) {
      remove(existingArticles, (existData) => {
        return existData?.key !== VehicleConfig.MODEL && existData?.key !== VehicleConfig.VARIANT;
      });
      existingArticles[keyIndex] = {
        key,
        priceExclVat,
        vatCode: vatCode as VatCode,
        id,
        articleType,
        dealerPriceExclVat,
        discountAmountExclVat,
        discountPercentage,
      };
    } else {
      existingArticles[keyIndex] = {
        key,
        priceExclVat,
        vatCode: vatCode as VatCode,
        id,
        articleType,
        dealerPriceExclVat,
        discountAmountExclVat,
        discountPercentage,
      };
    }
  } else if (!isRemove) {
    existingArticles?.push({
      key,
      priceExclVat,
      vatCode: vatCode as VatCode,
      id,
      articleType,
      dealerPriceExclVat,
      discountAmountExclVat,
      discountPercentage,
    });
  }
  return existingArticles?.filter((article) => article?.priceExclVat !== 0);
};

export const isArticleContains = (key: string, articles: INextArticle[], id: string = null) => {
  if (id) return articles?.findIndex((article) => article?.key === key && article?.id === id) >= 0;

  return articles?.findIndex((article) => article?.key === key) >= 0;
};

export const getColorInteriorCode = (order: IOrder) => {
  let varnishCode = null;
  let interiorCode = null;
  if (order?.vehicles?.[0]?.serialNo === '') {
    const factoryDetail = order?.vehicles?.[0]?.orderFromFactoryDetails;
    varnishCode = getVarnishCodeFromFactory(factoryDetail);
    interiorCode = getInteriorCodeFromFactory(factoryDetail);
  } else {
    varnishCode = getVarnishCodeFromOptions(order?.options);
    interiorCode = order?.options?.[0]?.packageInteriorId || getInteriorCodeFromOptions(order?.options);
  }
  return {
    varnishCode: varnishCode || null,
    interiorCode: interiorCode || null,
  };
};

export const isColorAvailableInArticle = (order: IOrder, currentColorId: string, articles: INextArticle[]) => {
  const { varnishCode } = getColorInteriorCode(order);
  if (!varnishCode) return !isArticleContains(VehicleConfig.COLOR, articles);
  if (currentColorId === varnishCode) {
    if (varnishCode) return isArticleContains(VehicleConfig.COLOR, articles, varnishCode);
    return true;
  }
  return false;
};

export const isInteriorAvailableInArticle = (order: IOrder, currentInteriorId: string, articles: INextArticle[]) => {
  const { interiorCode } = getColorInteriorCode(order);
  if (!interiorCode) return !isArticleContains(VehicleConfig.INTERIOR, articles);
  if (currentInteriorId === interiorCode) {
    if (interiorCode) return isArticleContains(VehicleConfig.INTERIOR, articles, interiorCode);
    return true;
  }
  return false;
};

export const isVariantAvailableInArticle = (variantId: string, articles: INextArticle[]) => {
  if (!variantId) return false;
  return isArticleContains(VehicleConfig.VARIANT, articles, variantId);
};

export const calculateVatForElCars = (
  baseArticles: INextArticle[],
  carType: string,
  vehicleCondition: VehicleCondition = VehicleCondition.NEW,
  targetedTotalPriceInclVatInclTaxDiscount: number = null
): ICalculatePricesBasedOnArticleListResult => {
  const articles = cloneDeep(baseArticles);
  articles.forEach((article) => {
    if (article.articleType === ArticleType.DELIVERY) {
      delete article['discountPercentage'];
    }
  });
  articles.map((article) => {
    if (
      (article?.articleType === ArticleType.IMPORTCONTRIBUTION ||
        article?.articleType === ArticleType.IMPORTCONTRIBUTION_PERCENTAGE) &&
      article?.dealerPriceExclVat != null &&
      article?.dealerPriceExclVat !== undefined
    ) {
      article.priceExclVat = article.dealerPriceExclVat;
    }
  });
  const vehicleApplicationCode = mapVehicleApplicationCodeStringToEnum(carType || '');
  try {
    const result = calculatePricesBasedOnArticleList({
      articles,
      vehicleApplicationCode,
      vehicleCondition,
      targetedTotalPriceInclVatInclTaxDiscount,
    });
    // Logs for VAT Calculation input/output
    console.log('\u001b[35m NPM Package: sales-common-calculations');
    console.log('\u001b[35m Function: calculatePricesBasedOnArticleList');
    console.log(
      ' Articles payload (Request Object) : ',
      `vehicleApplicationCode : ${vehicleApplicationCode}`,
      `vehicleCondition: ${vehicleCondition}`,
      articles?.map((article) => {
        return {
          key: article?.key,
          priceExclVat: article?.priceExclVat,
          vatCode: article?.vatCode,
          articleType: article?.articleType,
          dealerPriceExclVat: article?.dealerPriceExclVat,
          discountAmountExclVat: article?.discountAmountExclVat,
          discountPercentage: article?.discountPercentage,
        };
      })
    );
    console.log(' targetedTotalPriceInclVatInclTaxDiscount: ', targetedTotalPriceInclVatInclTaxDiscount);
    console.log(' Response: ', result);

    return result;
  } catch (error) {
    console.error('calculateVatForElCars func error:', error);
    return null;
  }
};

export const calculateVat = (
  articles: INextArticle[],
  carType: string,
  vehicleCondition: VehicleCondition,
  targetedTotalPriceInclVatInclTaxDiscount: number = null
): ICartPricesExtended => {
  try {
    const vatCalculation = calculateVatForElCars(
      articles,
      carType,
      vehicleCondition,
      targetedTotalPriceInclVatInclTaxDiscount
    );
    if (!vatCalculation) return null;
    return mapVatPricesForNext(vatCalculation) as any;
  } catch (error) {
    return null;
  }
};

export const getManufactureModelId = (
  vehicleData: IVehicleModel[],
  selectedModelId: string,
  variantId: string
): string => {
  const model = vehicleData?.find((model) => selectedModelId === model?.id) ?? null;
  const salesPackage = model?.salesPackages?.find((salesPackage) => variantId === salesPackage?.id) ?? null;

  return salesPackage?.manufacturerComponentIdModel ?? null;
};

export const getCarTypeOfSelectedModel = (modelData: IVehicleModel[], selectedModelId: string): string => {
  const model = modelData?.find((model) => selectedModelId === model?.id) ?? null;
  return model?.vehicleType || null;
};

export function getVatCalculationResult(props: IVatCalculationProps): ICartPricesCustom {
  const {
    vehicleCondition = VehicleCondition.NEW,
    targetedTotalPriceInclVatInclTaxDiscount = null,
    carType,
    articles,
  } = props;

  if (!articles?.length || isEmpty(carType)) {
    return null;
  }

  const vatPrices = calculateVat(articles, carType, vehicleCondition, targetedTotalPriceInclVatInclTaxDiscount);

  if (!vatPrices) {
    console.warn(getErrorMessageForError(ValidationErrorCode.VAT_VALIDATION));
    // @ts-expect-error
    return { ...(vatPrices || {}), error: true };
  }

  return { ...vatPrices, error: false };
}
